import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export interface ComboboxOption {
  id: number | string;
  label: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: number | string;
  onChange: (value: number | string, label: string) => void;
  onCreateOption?: (inputValue: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function Combobox({
  options,
  value,
  onChange,
  onCreateOption,
  placeholder = "Select an option",
  className = "",
  style = {},
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] =
    useState<ComboboxOption[]>(options);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update the input value when the selected value changes
  useEffect(() => {
    if (value) {
      const selectedOption = options.find((option) => option.id === value);
      if (selectedOption) {
        setInputValue(selectedOption.label);
        setHasUserTyped(false); // Reset when programmatically setting value
      }
    } else {
      setInputValue("");
      setHasUserTyped(false);
    }
  }, [value, options]);

  // Filter options based on whether user has typed
  useEffect(() => {
    if (!hasUserTyped) {
      // Show all options if user hasn't typed anything
      setFilteredOptions(options);
    } else {
      // Show filtered options only when user has typed
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options, hasUserTyped]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setHasUserTyped(true); // Mark that user has actively typed
    setIsOpen(true);
  };

  const handleOptionClick = (option: ComboboxOption) => {
    onChange(option.id, option.label);
    setInputValue(option.label);
    setHasUserTyped(false); // Reset when selecting an option
    setIsOpen(false);
  };

  const handleCreateOption = () => {
    if (onCreateOption && inputValue.trim()) {
      onCreateOption(inputValue.trim());
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onClick={() => {
            setIsOpen(true);
            setHasUserTyped(false); // Reset when clicking to open dropdown
          }}
          onFocus={() => {
            setIsOpen(true);
            setHasUserTyped(false); // Reset when focusing to open dropdown
          }}
          className={`form-default-size form-default-color px-3 w-full ${className}`}
          style={{
            ...style,
          }}
          placeholder={placeholder}
        />
        {options.length > 0 && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <ChevronUp className="text-tx-default" size={18} />
            ) : (
              <ChevronDown className="text-tx-default" size={18} />
            )}
          </button>
        )}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-10 mt-0 w-full rounded shadow-lg bg-white border border-bd-form"
          style={{
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          <div className="grid grid-cols-1 gap-0.5 py-1 px-1">
            {/* Options available */}

            {options.length > 0 &&
              (filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => handleOptionClick(option)}
                    className={`flex items-center px-3 py-3 text-sm cursor-pointer hover:font-semibold  hover:bg-accent-fg-cool ${
                      option.id === value
                        ? "font-semibold   bg-accent-fg-cool"
                        : "bg-transparent text-tx-dark"
                    }`}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-accent-bg-hot">
                  No options match your search
                </div>
              ))}

            {/* No options available */}
            {options.length === 0 && !hasUserTyped && (
              <div className="px-3 py-2 text-tx-muted text-sm">
                Type your own exercise name (e.g. "Squat")
              </div>
            )}

            {/* if user is inputting a new option*/}
            {onCreateOption &&
              hasUserTyped &&
              inputValue.trim() &&
              !options.some(
                (o) => o.label.toLowerCase() === inputValue.toLowerCase()
              ) && (
                <div
                  onClick={handleCreateOption}
                  className="flex flex-col gap-1 px-3 py-2 cursor-pointer bg-accent-fg-hot hover:bg-accent-fg-hot/50"
                >
                  <span className="text-tx-dark text-sm">
                    Click here to confirm{" "}
                  </span>{" "}
                  <span className="bg-accent-bg-hot text-accent-fg-hot px-2 py-1 w-fit text-sm rounded">
                    {inputValue} →
                  </span>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
